import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ownerDocumentOf, ownerWindowOf } from '../../utils/ownerWindow';
import { X, Save, Trash2, Loader2, AlertCircle, Pin } from 'lucide-react';
import { CalendarSkeleton } from '../shared/Skeleton';
import toast from 'react-hot-toast';
import { DailyCalendarGrid } from './DailyCalendarGrid';
import { SpreadPatternSelector } from './SpreadPatternSelector';
// eslint-disable-next-line import/no-relative-packages
import { formatCurrencyRounded } from '../../../../utils/currency';
import {
  getDailyValues,
  saveDailyValues,
  clearDailyValues,
  spreadMonthly,
  getDailyReference,
  getDriverDailyBreakdown,
  type DailyValue,
  type SpreadPattern,
  type GetDailyReferenceResponse,
  type DriverDailyBreakdownResponse,
  type DriverDailyBreakdownDay,
} from '../../api';

interface DailyEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  versionId: number;
  chartOfAccountId: number;
  locationId: number;
  accountName: string;
  accountNumber: string;
  driverType: string;
  driverValue: number; // The driver-calculated monthly value (revenue in stat mode)
  year: number;
  month: number; // 1-12
  currency?: string | null;       // ISO-4217 of the location being edited. Defaults to USD.
  onSave: (savedValues?: DailyValue[]) => void; // Callback to refresh grid after save; passes saved values in stat mode
  showStatMode?: boolean;         // When true: ADR + RN inputs per day, revenue computed
  linkedStatAccountName?: string; // e.g. "Best Available" — shown as badge in header
  initialMonthlyRN?: number;      // Monthly RN from stat account overview — used for even-spread pre-population
  initialMonthlyADR?: number;     // Monthly ADR from stat account overview — used for even-spread pre-population
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function formatCurrency(value: number, currency?: string | null): string {
  return formatCurrencyRounded(value, currency);
}

type ReferenceTab = 'none' | 'otb' | 'rms' | 'ly';

export function DailyEntryModal({
  isOpen,
  onClose,
  versionId,
  chartOfAccountId,
  locationId,
  accountName,
  accountNumber,
  driverType,
  driverValue,
  year,
  month,
  currency,
  onSave,
  showStatMode = false,
  linkedStatAccountName,
  initialMonthlyRN,
  initialMonthlyADR,
}: DailyEntryModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  // State
  const [dailyValues, setDailyValues] = useState<DailyValue[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pin-mode state (daily-capable drivers: OTB/RMS/REVPAR)
  const [breakdown, setBreakdown] = useState<DriverDailyBreakdownResponse | null>(null);
  const [convertRequested, setConvertRequested] = useState(false);
  // Days that were saved in the DB at load time — unpinning one must post a delete
  const dbSavedDaysRef = useRef<Set<number>>(new Set());

  // Reference data state
  const [referenceData, setReferenceData] = useState<GetDailyReferenceResponse | null>(null);
  const [activeReferenceTab, setActiveReferenceTab] = useState<ReferenceTab>('none');

  // Spread pattern state (only used in standard mode)
  const [selectedPattern, setSelectedPattern] = useState<SpreadPattern | null>(null);
  const [isApplyingPattern, setIsApplyingPattern] = useState(false);

  // Load initial data
  useEffect(() => {
    if (!isOpen) return;

    async function loadData() {
      setIsLoading(true);
      setError(null);
      setConvertRequested(false);

      try {
        const [valuesResponse, refResponse, breakdownResponse] = await Promise.all([
          getDailyValues({ versionId, chartOfAccountId, year, month }),
          getDailyReference({ locationId, year, month, chartOfAccountId }),
          getDriverDailyBreakdown({ versionId, chartOfAccountId, year, month })
            .catch(() => null), // older backend / any failure → classic behavior
        ]);

        const dailyCapable = !!breakdownResponse?.dailyCapable;
        dbSavedDaysRef.current = new Set(
          valuesResponse.values.filter(v => v.hasValue).map(v => v.day)
        );

        // In stat mode: if no daily data exists yet but monthly RN/ADR were provided,
        // pre-populate with an even spread of the monthly values across all days.
        // Skipped for daily-capable drivers — the live series IS the prefill there.
        let values = valuesResponse.values;
        if (!dailyCapable && showStatMode && initialMonthlyRN && initialMonthlyADR && values.length > 0) {
          const hasAnySavedStatValues = values.some(v => v.hasStatValues);
          if (!hasAnySavedStatValues) {
            const daysInMonth = values.length;
            const baseRN = Math.floor(initialMonthlyRN / daysInMonth);
            const remainder = initialMonthlyRN - baseRN * daysInMonth;
            values = values.map((v, i) => {
              const rn = baseRN + (i < remainder ? 1 : 0);
              const adr = initialMonthlyADR;
              return {
                ...v,
                roomNights: rn,
                adr: adr,
                amount: Math.round(adr * rn * 100) / 100,
                hasStatValues: rn > 0,
              };
            });
          }
        }

        setDailyValues(values);
        setReferenceData(refResponse);
        setBreakdown(breakdownResponse);
      } catch (err) {
        console.error('Failed to load daily values:', err);
        setError('Failed to load daily values. Please try again.');
      } finally {
        setIsLoading(false);
      }
    }

    loadData();
  }, [isOpen, versionId, chartOfAccountId, locationId, year, month, showStatMode, initialMonthlyRN, initialMonthlyADR]);

  // Pin mode is active when the driver is daily-capable and the month has no
  // legacy (static) rows — or the user chose to convert them to pins.
  const isLegacyMonth = breakdown?.dailyCapable === true && breakdown.monthMode === 'legacy' && !convertRequested;
  const pinMode = breakdown?.dailyCapable === true && !isLegacyMonth;

  // Per-day live driver series + realized days (read-only)
  const liveData = useMemo(() => {
    if (!pinMode || !breakdown) return undefined;
    const map = new Map<number, DriverDailyBreakdownDay['live']>();
    breakdown.days.forEach(d => map.set(d.day, d.live));
    return map;
  }, [pinMode, breakdown]);

  const actualDays = useMemo(() => {
    if (!pinMode || !breakdown) return undefined;
    return new Set(breakdown.days.filter(d => d.isActual).map(d => d.day));
  }, [pinMode, breakdown]);

  // Computed totals. In pin mode the month blends pinned days with live driver days.
  const isEnteredDay = useCallback(
    (v: DailyValue) => (showStatMode ? v.hasStatValues : v.hasValue),
    [showStatMode]
  );

  const pinnedTotal = useMemo(() => {
    if (!pinMode) return 0;
    return dailyValues
      .filter(v => isEnteredDay(v) && !actualDays?.has(v.day))
      .reduce((sum, v) => sum + (v.amount || 0), 0);
  }, [pinMode, dailyValues, isEnteredDay, actualDays]);

  const liveTotal = useMemo(() => {
    if (!pinMode || !liveData) return 0;
    let sum = 0;
    dailyValues.forEach(v => {
      if (!isEnteredDay(v) || actualDays?.has(v.day)) {
        sum += liveData.get(v.day)?.amount || 0;
      }
    });
    return sum;
  }, [pinMode, liveData, dailyValues, isEnteredDay, actualDays]);

  const monthlyTotal = useMemo(() => {
    if (pinMode) return pinnedTotal + liveTotal;
    return dailyValues.reduce((sum, v) => sum + (v.amount || 0), 0);
  }, [pinMode, pinnedTotal, liveTotal, dailyValues]);

  const monthlyRN = useMemo(() => {
    if (!showStatMode) return 0;
    if (pinMode && liveData) {
      return dailyValues.reduce((sum, v) => {
        if (isEnteredDay(v) && !actualDays?.has(v.day)) {
          return sum + (v.roomNights ?? (liveData.get(v.day)?.rooms || 0));
        }
        return sum + (liveData.get(v.day)?.rooms || 0);
      }, 0);
    }
    return dailyValues.reduce((sum, v) => sum + (v.roomNights || 0), 0);
  }, [dailyValues, showStatMode, pinMode, liveData, isEnteredDay, actualDays]);

  const weightedADR = useMemo(() => {
    if (!showStatMode || monthlyRN === 0) return 0;
    return monthlyTotal / monthlyRN;
  }, [showStatMode, monthlyTotal, monthlyRN]);

  const daysWithValues = useMemo(() => {
    return dailyValues.filter(isEnteredDay).length;
  }, [dailyValues, isEnteredDay]);

  const pinnedDayCount = useMemo(() => {
    if (!pinMode) return 0;
    return dailyValues.filter(v => isEnteredDay(v) && !actualDays?.has(v.day)).length;
  }, [pinMode, dailyValues, isEnteredDay, actualDays]);

  const liveDayCount = pinMode ? dailyValues.length - pinnedDayCount : 0;

  // Reference data map for revenue overlay
  const referenceDataMap = useMemo(() => {
    if (!referenceData) return undefined;

    const map = new Map<number, number>();

    switch (activeReferenceTab) {
      case 'otb':
        referenceData.otb.data.forEach((d) => map.set(d.day, d.otbRevenue));
        break;
      case 'rms':
        referenceData.rms.data.forEach((d) => map.set(d.day, d.revenue));
        break;
      case 'ly':
        referenceData.ly.data.forEach((d) => map.set(d.day, d.amount));
        break;
    }

    return map.size > 0 ? map : undefined;
  }, [referenceData, activeReferenceTab]);

  // Reference data map for room nights overlay (OTB/RMS only)
  const roomNightsReferenceMap = useMemo(() => {
    if (!referenceData || !showStatMode) return undefined;

    const map = new Map<number, number>();

    switch (activeReferenceTab) {
      case 'otb':
        referenceData.otb.data.forEach((d) => {
          if ((d as any).otbRooms != null) map.set(d.day, (d as any).otbRooms);
        });
        break;
      case 'rms':
        referenceData.rms.data.forEach((d) => {
          if ((d as any).roomsSold != null) map.set(d.day, (d as any).roomsSold);
        });
        break;
    }

    return map.size > 0 ? map : undefined;
  }, [referenceData, activeReferenceTab, showStatMode]);

  // Handle standard revenue change (in pin mode an entered value pins the day)
  const handleValueChange = useCallback((day: number, amount: number | null) => {
    setDailyValues((prev) =>
      prev.map((v) =>
        v.day === day
          ? { ...v, amount, hasValue: amount !== null, isPinned: pinMode ? amount !== null : v.isPinned }
          : v
      )
    );
    setIsDirty(true);
  }, [pinMode]);

  // Handle stat mode ADR + RN change — also computes revenue
  const handleStatValueChange = useCallback((day: number, adr: number | null, roomNights: number | null) => {
    setDailyValues((prev) =>
      prev.map((v) => {
        if (v.day !== day) return v;
        const hasStatValues = adr != null && roomNights != null;
        const amount = hasStatValues ? Math.round(adr * roomNights * 100) / 100 : null;
        return {
          ...v,
          adr,
          roomNights,
          amount,
          hasValue: amount !== null,
          hasStatValues,
          isPinned: pinMode ? hasStatValues : v.isPinned,
        };
      })
    );
    setIsDirty(true);
  }, [pinMode]);

  // Revert a pinned day to the live driver value
  const handleUnpin = useCallback((day: number) => {
    setDailyValues((prev) =>
      prev.map((v) =>
        v.day === day
          ? { ...v, amount: null, adr: null, roomNights: null, hasValue: false, hasStatValues: false, isPinned: false }
          : v
      )
    );
    setIsDirty(true);
  }, []);

  // Convert a legacy static month to pins (total unchanged until days are un-pinned)
  const handleConvertToPins = useCallback(() => {
    setConvertRequested(true);
    setDailyValues((prev) =>
      prev.map((v) => (v.hasValue ? { ...v, isPinned: true } : v))
    );
    setIsDirty(true);
  }, []);

  // Handle save
  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      let valuesToSave;

      if (pinMode) {
        // Pinned days are posted with values; days that were saved before but
        // are no longer entered post amount:null (delete → back to live).
        valuesToSave = dailyValues
          .filter((v) => isEnteredDay(v) || dbSavedDaysRef.current.has(v.day))
          .map((v) => {
            if (!isEnteredDay(v)) {
              return { day: v.day, amount: null };
            }
            return showStatMode && v.hasStatValues
              ? { day: v.day, amount: v.amount, adr: v.adr, roomNights: v.roomNights, notes: v.notes || undefined }
              : { day: v.day, amount: v.amount, notes: v.notes || undefined };
          });
      } else if (showStatMode) {
        valuesToSave = dailyValues
          .filter((v) => v.hasStatValues)
          .map((v) => ({
            day: v.day,
            amount: v.amount,
            adr: v.adr,
            roomNights: v.roomNights,
            notes: v.notes || undefined,
          }));
      } else {
        valuesToSave = dailyValues
          .filter((v) => v.hasValue || v.amount !== null)
          .map((v) => ({
            day: v.day,
            amount: v.amount,
            notes: v.notes || undefined,
          }));
      }

      if (valuesToSave.length === 0) {
        toast('Nothing to save');
        setIsSaving(false);
        return;
      }

      const result = await saveDailyValues({
        versionId,
        chartOfAccountId,
        locationId,
        year,
        month,
        values: valuesToSave,
        spreadPattern: showStatMode || pinMode ? 'manual' : (selectedPattern || 'manual'),
        pinMode,
        convert: pinMode && convertRequested,
      });

      if (pinMode && result.skippedPastDays) {
        toast(`${result.skippedPastDays} past day(s) skipped — already actualized`, { icon: 'ℹ️' });
      }
      toast.success(pinMode ? 'Pinned days saved' : 'Daily values saved');
      setIsDirty(false);
      setConvertRequested(false);
      onSave(showStatMode ? dailyValues : undefined);
      onClose();
    } catch (err: any) {
      console.error('Failed to save daily values:', err);
      const conflict = err?.response?.data;
      if (conflict?.code === 'LEGACY_DAILY_EXISTS' || conflict?.code === 'PINNED_DAILY_EXISTS') {
        toast.error(conflict.error || 'This month mixes static and pinned daily values.');
      } else {
        toast.error('Failed to save daily values');
      }
    } finally {
      setIsSaving(false);
    }
  }, [dailyValues, versionId, chartOfAccountId, locationId, year, month, selectedPattern, showStatMode, pinMode, convertRequested, isEnteredDay, onSave, onClose]);

  // Handle clear all
  const handleClearAll = useCallback(async () => {
    if (!ownerWindowOf(modalRef.current).confirm('Clear all daily values? This will revert to the driver calculation.')) {
      return;
    }

    setIsClearing(true);
    try {
      await clearDailyValues({ versionId, chartOfAccountId, year, month });

      setDailyValues((prev) =>
        prev.map((v) => ({ ...v, amount: null, adr: null, roomNights: null, hasValue: false, hasStatValues: false }))
      );
      setIsDirty(false);
      toast.success('Daily values cleared');
      onSave();
    } catch (err) {
      console.error('Failed to clear daily values:', err);
      toast.error('Failed to clear daily values');
    } finally {
      setIsClearing(false);
    }
  }, [versionId, chartOfAccountId, year, month, onSave]);

  // Handle apply spread pattern (standard mode only)
  const handleApplyPattern = useCallback(async () => {
    if (!selectedPattern) return;

    setIsApplyingPattern(true);
    try {
      const result = await spreadMonthly({
        versionId,
        chartOfAccountId,
        locationId,
        year,
        month,
        totalAmount: driverValue,
        pattern: selectedPattern,
        save: false,
      });

      setDailyValues((prev) =>
        prev.map((v) => {
          const spreadValue = result.values.find((sv) => sv.day === v.day);
          return {
            ...v,
            amount: spreadValue?.amount ?? null,
            hasValue: spreadValue?.amount !== null && spreadValue?.amount !== undefined,
          };
        })
      );
      setIsDirty(true);
      toast.success(`Applied "${selectedPattern}" pattern`);
    } catch (err) {
      console.error('Failed to apply spread pattern:', err);
      toast.error('Failed to apply spread pattern');
    } finally {
      setIsApplyingPattern(false);
    }
  }, [selectedPattern, versionId, chartOfAccountId, locationId, year, month, driverValue]);

  // Handle close with unsaved changes warning
  const handleClose = useCallback(() => {
    if (isDirty) {
      if (!ownerWindowOf(modalRef.current).confirm('You have unsaved changes. Are you sure you want to close?')) {
        return;
      }
    }
    onClose();
  }, [isDirty, onClose]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        handleClose();
      }
    }

    const doc = ownerDocumentOf(modalRef.current);
    doc.addEventListener('keydown', handleKeyDown);
    return () => doc.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  const variance = monthlyTotal - (breakdown?.totals?.driverMonthly ?? driverValue);

  return (
    <div ref={modalRef} className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="fv3-scrim absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="fv3-dialog relative bg-white rounded-[14px] shadow-[0_25px_60px_rgba(0,0,0,0.25)] w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col mx-4">
        {/* Header */}
        <div className="fv3-dialog-head flex items-center justify-between px-6 py-5 border-b border-slate-200">
          <div>
            <h2 className="fv3-dialog-title text-[17px] font-bold text-slate-900 tracking-tight">
              Daily Forecast Entry
            </h2>
            <div className="flex items-center gap-2 mt-1">
              <span className="font-mono text-[12px] text-slate-400">{accountNumber}</span>
              <span style={{ color: 'var(--fv3-n-200, #CBD5E1)' }}>·</span>
              <span className="text-[13px] font-medium text-slate-600">{accountName}</span>
              <span style={{ color: 'var(--fv3-n-200, #CBD5E1)' }}>·</span>
              <span className="text-[13px] text-slate-500">
                {MONTH_NAMES[month - 1]} {year}
              </span>
            </div>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {driverType && (
                <span className="text-[11px] px-[10px] py-[3px] bg-slate-100 text-slate-500 rounded-[5px] font-medium">
                  Driver: {driverType}
                </span>
              )}
              {pinMode && (
                <span className="flex items-center gap-1 text-[11px] px-[10px] py-[3px] bg-amber-50 text-amber-700 border border-amber-100 rounded-[5px] font-medium">
                  <Pin size={11} />
                  Pin days — unpinned days stay live
                  {breakdown?.usingFallbackDriver && breakdown.originalDepartmentDriverType
                    ? ` (${breakdown.originalDepartmentDriverType} fallback)`
                    : ''}
                </span>
              )}
              {showStatMode && linkedStatAccountName && (
                <span className="text-[11px] px-[10px] py-[3px] bg-blue-50 text-blue-600 border border-blue-100 rounded-[5px] font-medium">
                  Stat Account: {linkedStatAccountName}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 border border-slate-200 bg-white rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {isLoading ? (
            <>
              <div className="rn-sk-classic-only flex items-center justify-center h-64">
                <Loader2 size={32} className="animate-spin text-slate-400" />
              </div>
              <div className="rn-sk-inset-only"><CalendarSkeleton /></div>
            </>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-64 text-red-600">
              <AlertCircle size={32} className="mb-2" />
              <p>{error}</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-6">
              {/* Calendar Grid - Takes 2 columns */}
              <div className="col-span-2">
                {/* Legacy month on a live driver: offer conversion to pins */}
                {isLegacyMonth && (
                  <div className="flex items-start gap-3 mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <Pin size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 text-[12px] text-amber-800 leading-relaxed">
                      This month uses <strong>static daily values</strong> — the {breakdown?.usingFallbackDriver && breakdown.originalDepartmentDriverType ? breakdown.originalDepartmentDriverType : breakdown?.driverType} driver is
                      ignored entirely. Convert the entered days to <strong>pins</strong> to keep them fixed while
                      any days you un-pin flow live from the driver again.
                    </div>
                    <button
                      onClick={handleConvertToPins}
                      className="px-3 py-1.5 text-[12px] font-semibold text-amber-800 bg-white border border-amber-300 rounded-md hover:bg-amber-100 transition-colors flex-shrink-0"
                    >
                      Convert to pins
                    </button>
                  </div>
                )}
                {/* Reference tabs */}
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-xs text-slate-500 mr-2">Show reference:</span>
                  {(['none', 'otb', 'rms', 'ly'] as ReferenceTab[]).map((tab) => {
                    const isDisabled =
                      (tab === 'otb' && !referenceData?.otb.hasData) ||
                      (tab === 'rms' && !referenceData?.rms.hasData) ||
                      (tab === 'ly' && !referenceData?.ly.hasData);

                    return (
                      <button
                        key={tab}
                        onClick={() => setActiveReferenceTab(tab)}
                        disabled={isDisabled}
                        className={`px-3 py-1 text-xs font-medium rounded-md border transition-colors ${
                          activeReferenceTab === tab
                            ? 'bg-blue-50 border-blue-200 text-blue-600'
                            : isDisabled
                              ? 'bg-transparent border-slate-100 text-slate-300 cursor-not-allowed'
                              : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-700'
                        }`}
                      >
                        {tab === 'none' ? 'None' : tab.toUpperCase()}
                      </button>
                    );
                  })}
                </div>

                <DailyCalendarGrid
                  year={year}
                  month={month}
                  values={dailyValues}
                  onChange={handleValueChange}
                  onStatValueChange={showStatMode ? handleStatValueChange : undefined}
                  readOnly={false}
                  showStatMode={showStatMode}
                  referenceData={referenceDataMap}
                  referenceLabel={
                    activeReferenceTab === 'none'
                      ? undefined
                      : activeReferenceTab.toUpperCase()
                  }
                  roomNightsReferenceData={roomNightsReferenceMap}
                  roomNightsReferenceLabel={
                    activeReferenceTab !== 'none' && activeReferenceTab !== 'ly'
                      ? activeReferenceTab.toUpperCase()
                      : undefined
                  }
                  pinMode={pinMode}
                  liveData={liveData}
                  actualDays={actualDays}
                  onUnpin={pinMode ? handleUnpin : undefined}
                />
              </div>

              {/* Right sidebar */}
              <div className="space-y-4">
                {/* Summary */}
                <div className="bg-slate-50 rounded-[10px] p-[16px_18px] border border-slate-200">
                  <h3 className="text-[13px] font-bold text-slate-900 pb-[6px] mb-[10px] border-b border-slate-200">Summary</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between text-[13px]">
                      <span className="text-[12px] text-slate-500 font-normal">Driver Value</span>
                      <span className="font-medium">
                        {formatCurrency(breakdown?.totals?.driverMonthly ?? driverValue, currency)}
                      </span>
                    </div>

                    {pinMode && (
                      <>
                        <div className="flex justify-between text-[13px]">
                          <span className="text-[12px] text-slate-500 font-normal">
                            Pinned ({pinnedDayCount} {pinnedDayCount === 1 ? 'day' : 'days'})
                          </span>
                          <span className="font-semibold text-amber-700">
                            {formatCurrency(pinnedTotal, currency)}
                          </span>
                        </div>
                        <div className="flex justify-between text-[13px]">
                          <span className="text-[12px] text-slate-500 font-normal">
                            Live ({liveDayCount} {liveDayCount === 1 ? 'day' : 'days'})
                          </span>
                          <span className="font-semibold text-sky-600">
                            {formatCurrency(liveTotal, currency)}
                          </span>
                        </div>
                      </>
                    )}

                    {showStatMode ? (
                      <>
                        <div className="flex justify-between text-[13px]">
                          <span className="text-[12px] text-slate-500 font-normal">Monthly RN</span>
                          <span className="font-semibold text-[#1A8754]">
                            {monthlyRN.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between text-[13px]">
                          <span className="text-[12px] text-slate-500 font-normal">Avg ADR</span>
                          <span className="font-semibold text-[var(--fv3-accent-hover, #E8722A)]">
                            {weightedADR > 0 ? formatCurrency(weightedADR, currency) : '—'}
                          </span>
                        </div>
                        <div className="flex justify-between text-[13px]">
                          <span className="text-[12px] text-slate-500 font-normal">Revenue Total</span>
                          <span className="font-semibold text-[var(--fv3-accent-hover, #E8722A)]">
                            {formatCurrency(monthlyTotal, currency)}
                          </span>
                        </div>
                      </>
                    ) : (
                      <div className="flex justify-between text-[13px]">
                        <span className="text-[12px] text-slate-500 font-normal">Daily Total</span>
                        <span className="font-semibold text-[var(--fv3-accent-hover, #E8722A)]">
                          {formatCurrency(monthlyTotal, currency)}
                        </span>
                      </div>
                    )}

                    <div className="border-t border-slate-200 pt-2">
                      <div className="flex justify-between text-[13px]">
                        <span className="text-[12px] text-slate-500 font-normal">Variance</span>
                        <span
                          className={`font-medium ${
                            variance === 0
                              ? 'text-slate-500'
                              : variance > 0
                                ? 'text-green-600'
                                : 'text-red-600'
                          }`}
                        >
                          {variance >= 0 ? '+' : ''}{formatCurrency(variance, currency)}
                        </span>
                      </div>
                    </div>
                    <div className="text-[11px] text-slate-400 mt-2">
                      {pinMode
                        ? `${pinnedDayCount} pinned · ${liveDayCount} live from ${breakdown?.usingFallbackDriver && breakdown.originalDepartmentDriverType ? breakdown.originalDepartmentDriverType : (breakdown?.driverType || driverType)}`
                        : `${daysWithValues} of ${dailyValues.length} days entered`}
                    </div>
                  </div>
                </div>

                {/* Spread Pattern Selector (standard mode only) */}
                {pinMode ? (
                  <div className="bg-sky-50 rounded-[7px] p-[10px_12px] border border-sky-100 mt-1">
                    <p className="text-[11px] leading-relaxed text-slate-500">
                      Enter a value on a day to <strong>pin</strong> it — pinned days stay fixed while every
                      other day keeps updating from live data. Use the ⟲ icon on a pinned day to hand it
                      back to the driver. Pins apply to this account only.
                    </p>
                  </div>
                ) : showStatMode ? (
                  <div className="bg-orange-50 rounded-[7px] p-[10px_12px] border border-orange-100 mt-1">
                    <p className="text-[11px] leading-relaxed" style={{ color: 'var(--fv3-n-400, #94A3B8)' }}>
                      Enter ADR and Room Nights per day. Revenue is computed automatically (ADR × RN). Spread patterns are not available in stat account mode.
                    </p>
                  </div>
                ) : (
                  <SpreadPatternSelector
                    selectedPattern={selectedPattern}
                    onSelect={setSelectedPattern}
                    onApply={handleApplyPattern}
                    isApplying={isApplyingPattern}
                    totalAmount={driverValue}
                    currency={currency}
                    hasLyData={referenceData?.ly.hasData}
                    hasOtbData={referenceData?.otb.hasData}
                  />
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="fv3-dialog-foot flex items-center justify-between px-6 py-3.5 border-t border-slate-100 bg-slate-50 flex-shrink-0">
          <button
            onClick={handleClearAll}
            disabled={isClearing || daysWithValues === 0}
            className={`flex items-center gap-1.5 px-3 py-2 text-[13px] font-medium rounded-lg transition-colors ${
              daysWithValues > 0
                ? 'text-red-500 hover:text-red-600 hover:bg-red-50'
                : 'text-slate-300 cursor-not-allowed'
            }`}
          >
            {isClearing ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Trash2 size={16} />
            )}
            {pinMode ? 'Clear All Pins' : 'Clear All'}
          </button>

          <div className="flex items-center gap-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-[13px] font-medium text-slate-500 border border-slate-200 bg-white hover:bg-slate-50 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!isDirty || isSaving}
              className={`flex items-center gap-2 px-4 py-2 text-[13px] font-semibold rounded-lg transition-all ${
                isDirty
                  ? 'text-white shadow-[0_2px_8px_rgba(232,114,42,0.3)]'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }`}
              style={isDirty ? { background: 'linear-gradient(135deg, var(--fv3-accent, #F28C38), var(--fv3-accent-hover, #E8722A))' } : undefined}
            >
              {isSaving ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Save size={16} />
              )}
              {pinMode ? 'Save Pinned Days' : 'Save Daily Values'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
